import type { AuthProviderContext, AuthProviderProps } from '@newfront-insurance/next-auth';
import type { Provider } from '@newfront-insurance/react-provision';
import { useProvider } from '@newfront-insurance/react-provision';
import type { ConfigType } from '@newfront-insurance/shared-public-config';
import { useSuspenseQuery } from '@tanstack/react-query';
import { asyncWithLDProvider, useLDClient, type LDClient, type LDContext } from 'launchdarkly-react-client-sdk';
import type { JSX } from 'react';
import { useEffect } from 'react';

interface Props {
  children: React.ReactNode;
  config: ConfigType;
  authProvider: Provider<AuthProviderContext, AuthProviderProps>;
}

/**
 * Initialises LaunchDarkly with the user available at startup, then synchronizes identity
 * as authentication resolves or changes without replacing the SDK provider or its children.
 */
export function FeatureFlagProvider({ children, authProvider, config }: Props): JSX.Element | null {
  const { LAUNCH_DARKLY } = config;
  const { userDetails: user } = useProvider(authProvider);

  const { data: LDAsyncProvider } = useSuspenseQuery({
    queryKey: ['LaunchDarkly'],
    staleTime: Infinity,
    gcTime: Infinity,
    queryFn: () => {
      return asyncWithLDProvider({
        clientSideID: LAUNCH_DARKLY.PROJECTS.ADMIN_DASH,
        options: { bootstrap: 'localStorage' },
        reactOptions: { useCamelCaseFlagKeys: false },
        context: user
          ? {
              email: user.email,
              firstName: user.firstName,
              lastName: user.lastName,
              key: user.uuid,
              anonymous: false,
            }
          : { anonymous: true },
      });
    },
    refetchInterval: 0,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
  });
  if (!LDAsyncProvider) {
    return null;
  }
  return (
    <LDAsyncProvider>
      <FeatureFlagUserProvider authProvider={authProvider}>{children}</FeatureFlagUserProvider>
    </LDAsyncProvider>
  );
}

function FeatureFlagUserProvider({ children, authProvider }: Pick<Props, 'children' | 'authProvider'>): JSX.Element {
  const { authState, userDetails } = useProvider(authProvider);
  const { isReady, isLoggedIn } = authState;
  const { uuid, email, firstName, lastName } = userDetails ?? {};
  const client = useLDClient();

  useEffect(() => {
    if (!client || !isReady || (isLoggedIn && !uuid)) return;

    let synchronize = identitySynchronizers.get(client);
    if (!synchronize) {
      synchronize = createIdentitySynchronizer(client);
      identitySynchronizers.set(client, synchronize);
    }

    return synchronize(isLoggedIn ? { key: uuid, email, firstName, lastName, anonymous: false } : { anonymous: true });
  }, [client, isReady, isLoggedIn, uuid, email, firstName, lastName]);

  // Nova's embed starts authentication below this provider, so synchronization must not gate rendering.
  return <>{children}</>;
}

type IdentitySynchronizer = (context: LDContext) => () => void;

// The cached SDK client can outlive the React component, including while identify() is pending.
const identitySynchronizers = new WeakMap<LDClient, IdentitySynchronizer>();

function getContextKey(context: LDContext | undefined): string {
  if (!context) return '';

  return JSON.stringify({
    kind: 'kind' in context ? context.kind : 'user',
    anonymous: context.anonymous === true,
    // LaunchDarkly generates/persists anonymous keys; they are not application targeting attributes.
    key: context.anonymous ? undefined : context.key,
    email: context.email,
    firstName: context.firstName,
    lastName: context.lastName,
  });
}

function createIdentitySynchronizer(client: LDClient): IdentitySynchronizer {
  const subscribers = new Map<symbol, LDContext>();
  let appliedContextKey = getContextKey(client.getContext());
  let identifying = false;

  // The latest active subscription wins; an earlier one resumes when a newer one unmounts.
  const latestContext = () => [...subscribers.values()].pop();

  const identifyLatestContext = async (): Promise<void> => {
    const context = latestContext();
    if (!context || identifying) return;

    const requestedContextKey = getContextKey(context);
    if (requestedContextKey === appliedContextKey) return;

    identifying = true;
    try {
      await client.identify(context);
      appliedContextKey = getContextKey(client.getContext());
    } catch {
      // The SDK reports failures. Preserve current flags and allow a later auth change/remount to retry.
    } finally {
      identifying = false;
    }

    const nextContext = latestContext();
    // Remounts during a request share its outcome. The same failed target needs a new
    // auth change or remount after settlement, rather than an automatic retry here.
    if (nextContext && getContextKey(nextContext) !== requestedContextKey) {
      // identify() updates the SDK itself after fetching, so requests must finish in order.
      void identifyLatestContext();
    }
  };

  return (context) => {
    const subscriber = Symbol();
    subscribers.set(subscriber, context);
    void identifyLatestContext();

    return () => {
      subscribers.delete(subscriber);
      // Do not reset the in-flight guard: Strict Mode or a real remount may reuse this client.
      void identifyLatestContext();
    };
  };
}
